"use client";
import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { EyeIcon, EyeOff, X, Calendar } from "lucide-react";
import { CustomTooltip } from "./tooltip";

interface InputCustomProps {
  onClear?: () => void;
  tooltip?: string;
  value?: string | number;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  error?: string | null | undefined | boolean;
  variant?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "error"
    | "outline";
  containerClassName?: string;
  icon?: React.ReactNode;
  type?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  rows?: number;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  autoComplete?: string;
  required?: boolean;
  min?: number | string;
  max?: number | string;
  // Locks the show/hide toggle for type="password": greys it out, blocks
  // clicks, and forces the value back to hidden. Used for one-shot secrets
  // (e.g. a temporary password) that must not be re-revealed after copying.
  toggleDisabled?: boolean;
  // Native passthrough props forwarded to the underlying <input>/<textarea>
  // via {...props} in the render branches below.
  maxLength?: number;
  defaultValue?: string | number | readonly string[];
  accept?: string;
  readOnly?: boolean;
  pattern?: string;
  step?: number | string;
  autoFocus?: boolean;
  multiple?: boolean;
  onInput?: React.FormEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onPaste?: React.ClipboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onClick?: React.MouseEventHandler<HTMLInputElement | HTMLTextAreaElement> | (() => void);
  onDoubleClick?: React.MouseEventHandler<HTMLInputElement | HTMLTextAreaElement> | (() => void);
  ref?: React.Ref<HTMLInputElement & HTMLTextAreaElement>;
}

const InputCustom = React.forwardRef<
  HTMLInputElement & HTMLTextAreaElement,
  InputCustomProps
>(function InputCustom(
  {
    value,
    onChange,
    onClear,
    className,
    tooltip,
    label,
    disabled,
    error,
    variant = "outline",
    containerClassName,
    icon,
    type,
    name,
    rows = 3,
    min,
    max,
    toggleDisabled,
    ...props
  },
  ref,
) {
  const variantClasses = {
    default:
      "flex items-center justify-between rounded-lg px-3 py-2 transition-all duration-300 focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50 border border-transparent bg-slate-100",
    primary:
      "w-full border border-primary rounded-md text-black placeholder-gray-500 focus:outline-none focus:border-primary",
    secondary: "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
    success: "border-green-300 focus:ring-green-500 focus:border-green-500",
    warning: "border-yellow-300 focus:ring-yellow-500 focus:border-yellow-500",
    error: "border-red-300 focus:ring-red-500 focus:border-red-500",
    outline:
      "w-full border border-primary rounded-md text-black placeholder-gray-400 focus:outline-none focus:border-primary",
  };

  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const isDate = type === "date";

  // Force the value back to hidden the moment the toggle gets locked.
  useEffect(() => {
    if (toggleDisabled) setShowPassword(false);
  }, [toggleDisabled]);

  const [inputValue, setInputValue] = useState("");
  const lastPropsValue = useRef<string | number | undefined>(undefined);

  // Nombre del archivo seleccionado (para type="file")
  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    if (isDate) {
      if (value !== lastPropsValue.current) {
        lastPropsValue.current = value;
        if (value) {
          const parts = String(value).split("-");
          if (parts.length === 3) {
            const [year, month, day] = parts;
            setInputValue(`${day}/${month}/${year}`);
            return;
          }
        }
        setInputValue("");
      }
    }
  }, [value, isDate]);

  // Si el padre limpia el value (por ejemplo con onClear), reseteamos el nombre del archivo
  useEffect(() => {
    if (type === "file" && (value === "" || value === null)) {
      setFileName(null);
    }
  }, [value, type]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    const digits = rawVal.replace(/\D/g, "");

    let formatted = "";
    if (digits.length > 0) {
      formatted += digits.substring(0, 2);
    }
    if (digits.length > 2) {
      formatted += "/" + digits.substring(2, 4);
    }
    if (digits.length > 4) {
      formatted += "/" + digits.substring(4, 8);
    }

    const limited = formatted.substring(0, 10);
    setInputValue(limited);

    // If completely empty
    if (digits.length === 0) {
      lastPropsValue.current = "";
      if (onChange) {
        onChange({
          target: { value: "", name },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
      }
      return;
    }

    // If typing and incomplete, but was previously complete
    if (digits.length < 8 && lastPropsValue.current !== "") {
      lastPropsValue.current = "";
      if (onChange) {
        onChange({
          target: { value: "", name },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
      }
      return;
    }

    // If complete (8 digits), validate and propagate YYYY-MM-DD
    if (digits.length === 8) {
      const day = digits.substring(0, 2);
      const month = digits.substring(2, 4);
      const year = digits.substring(4, 8);

      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);

      if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
        const iso = `${year}-${month}-${day}`;

        // Respect min/max bounds when the date is typed manually
        if (min && iso < String(min)) return;
        if (max && iso > String(max)) return;

        lastPropsValue.current = iso;
        if (onChange) {
          onChange({
            target: { value: iso, name },
          } as unknown as React.ChangeEvent<HTMLInputElement>);
        }
      }
    }
  };

  return (
    <div className={cn("w-full flex flex-col gap-1.5", containerClassName)}>
      {label && <p className={cn("mb-0 text-sm")}>{label}</p>}
      <div
        className={cn(
          variantClasses[variant],
          error &&
            "border-red-500 ring-red-500 focus-within:ring-red-500 focus-within:border-red-500",
          "px-2 flex items-center transition-all",
          type === "textArea" ? "min-h-[100px] py-1" : "h-10",
          disabled && "opacity-70 bg-gray-100",
          className,
        )}
      >
        {icon && <div className="mr-2 opacity-50">{icon}</div>}
        {type === "textArea" ? (
          <textarea
            {...props}
            ref={ref as unknown as React.Ref<HTMLTextAreaElement>}
            name={name}
            value={value}
            onChange={onChange}
            rows={rows}
            disabled={disabled}
            className={cn(
              "flex-1 bg-transparent border-none outline-none ring-0 focus:ring-0 text-sm text-slate-700",
              disabled && " cursor-not-allowed",
            )}
          />
        ) : isDate ? (
          <div className="flex-1 min-w-0 flex items-center relative w-full h-full">
            <input
              type="text"
              name={name}
              placeholder="dd/mm/aaaa"
              value={inputValue}
              onChange={handleTextChange}
              disabled={disabled}
              className={cn(
                "flex-1 min-w-0 bg-transparent border-none outline-none ring-0 focus:ring-0 text-sm text-slate-700 h-full",
                disabled && "cursor-not-allowed",
              )}
            />
            <div className="relative flex items-center justify-center w-6 h-6 mr-1 shrink-0 overflow-hidden">
              <Calendar className="text-slate-400 size-4 pointer-events-none" />
              <input
                type="date"
                name={name}
                value={value || ""}
                onChange={onChange}
                disabled={disabled}
                min={min}
                max={max}
                className="absolute inset-0 opacity-0 cursor-pointer"
                style={{
                  width: "24px",
                  height: "24px",
                  maxWidth: "24px",
                  maxHeight: "24px",
                }}
              />
            </div>
          </div>
        ) : type === "file" ? (
          <label
            className={cn(
              "flex-1 flex items-center gap-2 w-full h-full cursor-pointer overflow-hidden",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <span
              className={cn(
                "shrink-0 text-[11px] font-bold bg-blue-50 text-primary px-3 py-1 rounded-md transition-colors",
                !disabled && "hover:bg-blue-100",
              )}
            >
              Elegir archivo
            </span>
            <span className="text-xs text-slate-500 truncate">
              {fileName ?? "Ningún archivo seleccionado"}
            </span>
            <input
              {...props}
              ref={ref as unknown as React.Ref<HTMLInputElement>}
              name={name}
              onChange={(e) => {
                setFileName(e.target.files?.[0]?.name ?? null);
                onChange?.(e);
              }}
              disabled={disabled}
              type="file"
              className="hidden"
            />
          </label>
        ) : (
          <input
            {...props}
            ref={ref as unknown as React.Ref<HTMLInputElement>}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            min={min}
            max={max}
            type={isPassword && showPassword ? "text" : type}
            className={cn(
              "flex-1 min-w-0 bg-transparent border-none outline-none ring-0 focus:ring-0 text-sm text-slate-700",
              disabled && "cursor-not-allowed",
            )}
          />
        )}
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            disabled={toggleDisabled}
            className="ml-2 rounded-md p-1 text-slate-400 hover:text-slate-600 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={
              showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
            }
          >
            {showPassword ? (
              <EyeOff size={16} className="text-primary mr-2" />
            ) : (
              <EyeIcon size={16} className="text-primary mr-2" />
            )}
          </button>
        )}
        {onClear && (
          <div className="ml-2">
            {tooltip ? (
              <CustomTooltip content={tooltip}>
                <button
                  type="button"
                  onClick={onClear}
                  className="rounded-md p-1 text-slate-400 hover:text-red-500 transition-colors"
                  aria-label="Limpiar campo"
                >
                  <X size={16} />
                </button>
              </CustomTooltip>
            ) : (
              <button
                type="button"
                onClick={onClear}
                className="rounded-md p-1 text-slate-400 hover:text-red-500 transition-colors"
                aria-label="Limpiar campo"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
});

InputCustom.displayName = "InputCustom";

export default InputCustom;
